// Generates a high-entropy recovery passphrase that the user copies
// (or downloads) once at signup time. Used by the auth recovery
// setup screen and by the Settings "rotate backup" affordance.
//
// Format: six groups of four base32-Crockford characters, separated
// by hyphens (e.g. "XK4P-RZ8N-MFQ7-LWBD-T2HG-J6XV"). 24 char-payload
// = 120 bits of entropy. Crockford base32 excludes the visually
// confusable I/L/O/U so users can hand-type the key off a printed
// backup without ambiguity. Hyphens are display-only — `normalise()`
// strips them (and case) before the passphrase is fed into the
// PBKDF2 KDF in `recoveryAead`.
//
// Why not BIP-39 wordlists? Two reasons:
//   * Bundle size: BIP-39's 2048-word list is ~16 KB, on top of an
//     auth surface we want to keep slim.
//   * Phishing resistance: a random base32 token is unmistakably a
//     secret. A 12-word "memorable" mnemonic encourages users to
//     paste it into anyone-asking-for-a-mnemonic prompts.
// The grouped-base32 format is what WhatsApp, GitHub recovery codes,
// and most enterprise account-recovery flows use.
//
// SECURITY: the random bytes come from the platform CSPRNG
// (`crypto.getRandomValues`). Never replace this with `Math.random`.

const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const GROUP_COUNT = 6;
const CHARS_PER_GROUP = 4;
const SEPARATOR = '-';

/**
 * Number of random bits the generated passphrase encodes.
 * 6 groups * 4 chars * log2(32) = 120 bits.
 */
export const RECOVERY_PASSPHRASE_BITS = GROUP_COUNT * CHARS_PER_GROUP * 5;

/** Display length, including separators (e.g. 29 for 6-group format). */
export const RECOVERY_PASSPHRASE_DISPLAY_LEN =
  GROUP_COUNT * CHARS_PER_GROUP + (GROUP_COUNT - 1);

/**
 * Returns a fresh recovery passphrase formatted for display.
 *
 * The string is safe to render directly: it contains only A-Z, 0-9,
 * and the hyphen separator. Pass it into `normaliseRecoveryPassphrase`
 * before handing it to the encryption KDF.
 */
export function generateRecoveryPassphrase(): string {
  const totalChars = GROUP_COUNT * CHARS_PER_GROUP;
  const indices = new Uint8Array(totalChars);
  fillRandomUint8(indices);
  const out: string[] = [];
  for (let g = 0; g < GROUP_COUNT; g++) {
    let group = '';
    for (let c = 0; c < CHARS_PER_GROUP; c++) {
      const idx = indices[g * CHARS_PER_GROUP + c]! % CROCKFORD_ALPHABET.length;
      group += CROCKFORD_ALPHABET.charAt(idx);
    }
    out.push(group);
  }
  return out.join(SEPARATOR);
}

/**
 * Normalises a user-typed passphrase for crypto operations:
 *   * uppercases (Crockford alphabet is single-case)
 *   * strips whitespace and hyphens
 *   * maps the Crockford "alias" characters (I/L → 1, O → 0) so a
 *     handwritten transcription "XK4O-RZBN" still decrypts.
 *
 * Returns `null` if any character is outside the alphabet after the
 * alias map — callers surface this as "doesn't look like a recovery
 * key" before attempting decryption (which would otherwise fail with
 * a generic "wrong passphrase").
 */
export function normaliseRecoveryPassphrase(raw: string): string | null {
  let normalised = '';
  for (const ch of raw.toUpperCase()) {
    if (ch === ' ' || ch === '-' || ch === '\t' || ch === '\n') continue;
    const mapped = ch === 'I' || ch === 'L' ? '1' : ch === 'O' ? '0' : ch;
    if (!CROCKFORD_ALPHABET.includes(mapped)) return null;
    normalised += mapped;
  }
  if (normalised.length !== GROUP_COUNT * CHARS_PER_GROUP) return null;
  return normalised;
}

function fillRandomUint8(out: Uint8Array): void {
  const g = globalThis as {
    crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array };
  };
  if (g.crypto?.getRandomValues) {
    g.crypto.getRandomValues(out);
    return;
  }
  throw new Error(
    'recoveryPassphrase: no CSPRNG available (globalThis.crypto.getRandomValues missing)',
  );
}
